import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../api/api";

import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  useMediaQuery,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import trackingImg from "./mamazealyntwo.jpg";

import { PDFDownloadLink } from "@react-pdf/renderer";
import ShipmentReceipt from "./ShipmentReceipt";

// ===============================
const steps = [
  { key: "PICKUP", label: "Pickup", icon: <Inventory2Icon /> },
  { key: "LOADING", label: "Loading", icon: <LocalShippingIcon /> },
  { key: "TRANSIT", label: "Transit", icon: <DirectionsBoatIcon /> },
  { key: "ARRIVED", label: "Arrival", icon: <WarehouseIcon /> },
  { key: "READY", label: "Ready", icon: <CheckCircleIcon /> },
  { key: "CLOSED", label: "Closed", icon: <CheckCircleIcon /> },
];

const TrackingContainer = () => {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");

  const [loading, setLoading] = useState(true);
  const [shipment, setShipment] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchTracking = async () => {
      try {
        let url = `/good-shipments/track/?code=${code}`;
        if (email) url += `&email=${email}`;

        const res = await api.get(url);
        setShipment(res.data);
      } catch (err) {
        console.log(err);
        setShipment(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTracking();
  }, [code, email]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!shipment) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Typography variant="h6">Tracking information not found.</Typography>
      </Box>
    );
  }

  const currentStatus = shipment.trip_status || shipment.tracking_status;

  const currentStepIndex = Math.max(
    0,
    steps.findIndex((s) => s.key === currentStatus)
  );

  return (
    <Box sx={{ minHeight: "100vh", background: "#f4f7fb", py: 5, px: 2 }}>
      <Paper sx={{ maxWidth: 1100, mx: "auto", p: 4, borderRadius: 4 }}>

        {/* HEADER */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <DirectionsBoatIcon sx={{ fontSize: 50, color: "#f59e0b" }} />

          <Box>
            <Typography variant="h4" fontWeight="bold">
              Shipment Tracking
            </Typography>

            <Typography color="text.secondary">
              Tracking Code: {shipment.tracking_code}
            </Typography>
          </Box>
        </Box>

        {/* STATUS */}
        <Chip
          label={currentStatus}
          sx={{
            mb: 3,
            background: "#f59e0b",
            color: "#fff",
            fontWeight: "bold",
          }}
        />

        {/* IMAGE + INFO */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, mb: 5 }}>

          {/* IMAGE */}
          <Paper sx={{ flex: 1, borderRadius: 3, overflow: "hidden" }}>
            <Box
              component="img"
              src={trackingImg}
              sx={{ width: "100%", height: { xs: 180, md: 260 }, objectFit: "cover" }}
            />

            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Real-Time Shipment Tracking
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Track your shipment from pickup to delivery in real time.
              </Typography>
            </Box>
          </Paper>

          {/* INFO */}
          <Paper sx={{ flex: 1, p: 3, borderRadius: 3 }}>
            <Typography fontWeight="bold">Shipment Details</Typography>
            <Divider sx={{ my: 2 }} />

            <Typography><b>Status:</b> {currentStatus}</Typography>
            <Typography><b>Sender:</b> {shipment.customer_name}</Typography>
            <Typography><b>Receiver:</b> {shipment.receiver_name}</Typography>
            <Typography><b>Trip:</b> {shipment.trip || "N/A"}</Typography>

    
           <Box sx={{ mt: 3 }}>
  <PDFDownloadLink
    document={<ShipmentReceipt shipment={shipment} />}
    fileName={`shipment-${shipment.tracking_code}.pdf`}
    style={{ textDecoration: "none" }}
  >
    {({ loading }) => (
      <Box
        sx={{
          background: "#111827",
          color: "#fff",
          textAlign: "center",
          py: 1.5,
          borderRadius: 2,
          cursor: "pointer",
        }}
      >
        {loading ? "Generating..." : "Download Receipt"}
      </Box>
    )}
  </PDFDownloadLink>
</Box>
          </Paper>
        </Box>

        {/* ================= DESKTOP TRACKER (FIXED) ================= */}
        {!isMobile && (
          <Box sx={{ position: "relative", mb: 6 }}>

            {/* SEGMENTED LINE */}
            <Box
              sx={{
                position: "absolute",
                top: 30,
                left: "5%",
                right: "5%",
                height: 5,
                display: "flex",
                zIndex: 1,
              }}
            >
              {steps.map((_, index) => {
                const active = index < currentStepIndex;
                return (
                  <Box
                    key={index}
                    sx={{
                      flex: 1,
                      background: active ? "#f59e0b" : "#e5e7eb",
                      transition: "0.4s",
                    }}
                  />
                );
              })}
            </Box>

            {/* STEPS */}
            <Box sx={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 2 }}>
              {steps.map((step, index) => {
                const isActive = index === currentStepIndex;
                const isPassed = index < currentStepIndex;

                return (
                  <Box key={step.key} sx={{ textAlign: "center", flex: 1 }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        mx: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",

                        border: (isPassed || isActive)
                          ? "2px solid #f59e0b"
                          : "2px solid #d1d5db",

                        background: "#fff",
                        color: (isPassed || isActive) ? "#f59e0b" : "#9ca3af",

                        boxShadow: isActive
                          ? "0 0 0 4px rgba(245,158,11,0.15)"
                          : "none",
                      }}
                    >
                      {step.icon}
                    </Box>

                    <Typography sx={{ mt: 1, fontWeight: isActive ? "bold" : 400 }}>
                      {step.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* ================= MOBILE (UNCHANGED STYLE) ================= */}
        {isMobile && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {steps.map((step, index) => {
              const isActive = index === currentStepIndex;
              const isPassed = index < currentStepIndex;

              return (
                <Box
                  key={step.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    background: isActive ? "#fff7ed" : isPassed ? "#eff6ff" : "#f3f4f6",
                  }}
                >
                  <Box
                    sx={{
                      width: 45,
                      height: 45,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: (isPassed || isActive)
                        ? "2px solid #f59e0b"
                        : "2px solid #d1d5db",
                      color: (isPassed || isActive) ? "#f59e0b" : "#9ca3af",
                    }}
                  >
                    {step.icon}
                  </Box>

                  <Typography>{step.label}</Typography>
                </Box>
              );
            })}
          </Box>
        )}

      </Paper>
    </Box>
  );
};

export default TrackingContainer;


// import React, { useEffect, useState } from "react";
// import { useParams, useSearchParams } from "react-router-dom";
// import api from "../api/api";

// import {
//   Box,
//   Typography,
//   CircularProgress,
//   Paper,
//   Grid,
//   Divider,
//   Chip,
//   useMediaQuery,
// } from "@mui/material";

// import { useTheme } from "@mui/material/styles";

// import Inventory2Icon from "@mui/icons-material/Inventory2";
// import LocalShippingIcon from "@mui/icons-material/LocalShipping";
// import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
// import WarehouseIcon from "@mui/icons-material/Warehouse";
// import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// import trackingImg from "./mamazealyntwo.jpg";

// import { PDFDownloadLink } from "@react-pdf/renderer";
// import ShipmentReceipt from "./ShipmentReceipt";

// // ===============================
// const steps = [
//   { key: "PICKUP", label: "Pickup", icon: <Inventory2Icon /> },
//   { key: "LOADING", label: "Loading", icon: <LocalShippingIcon /> },
//   { key: "TRANSIT", label: "Transit", icon: <DirectionsBoatIcon /> },
//   { key: "ARRIVED", label: "Arrival", icon: <WarehouseIcon /> },
//   { key: "READY", label: "Ready", icon: <CheckCircleIcon /> },
//   { key: "CLOSED", label: "Closed", icon: <CheckCircleIcon /> },
// ];

// const TrackingContainer = () => {
//   const { code } = useParams();
//   const [searchParams] = useSearchParams();
//   const email = searchParams.get("email");

//   const [loading, setLoading] = useState(true);
//   const [shipment, setShipment] = useState(null);

//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

//   useEffect(() => {
//     const fetchTracking = async () => {
//       try {
//         let url = `/good-shipments/track/?code=${code}`;
//         if (email) url += `&email=${email}`;

//         const res = await api.get(url);
//         setShipment(res.data);
//       } catch (err) {
//         console.log(err);
//         setShipment(null);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchTracking();
//   }, [code, email]);

//   if (loading) {
//     return (
//       <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
//         <CircularProgress />
//       </Box>
//     );
//   }

//   if (!shipment) {
//     return (
//       <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
//         <Typography variant="h6">Tracking information not found.</Typography>
//       </Box>
//     );
//   }

//   const currentStatus = shipment.trip_status || shipment.tracking_status;

//   const currentStepIndex = steps.findIndex(
//     (step) => step.key === currentStatus
//   );

//   return (
//     <Box sx={{ minHeight: "100vh", background: "#f4f7fb", py: 5, px: 2 }}>
//       <Paper sx={{ maxWidth: 1100, mx: "auto", p: 4, borderRadius: 4 }}>

//         {/* ================= HEADER ================= */}
//         <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
//           <DirectionsBoatIcon sx={{ fontSize: 50, color: "#f59e0b" }} />

//           <Box>
//             <Typography
//               variant="h4"
//               fontWeight="bold"
//               sx={{
//                 fontSize: { xs: "1.4rem", sm: "2.125rem" },
//                 lineHeight: 1.2,
//               }}
//             >
//               Shipment Tracking
//             </Typography>

//             <Typography
//               color="text.secondary"
//               sx={{
//                 fontSize: { xs: "0.85rem", sm: "1rem" },
//               }}
//             >
//               Tracking Code: {shipment.tracking_code}
//             </Typography>
//           </Box>
//         </Box>

//         {/* ================= STATUS ================= */}
//         <Chip
//           label={currentStatus}
//           sx={{
//             mb: 3,
//             background: "#f59e0b",
//             color: "#fff",
//             fontWeight: "bold",
//           }}
//         />

//         {/* ================= IMAGE + INFO CARD SECTION ================= */}
//         <Box
//           sx={{
//             display: "flex",
//             flexDirection: { xs: "column", md: "row" },
//             gap: 3,
//             mb: 5,
//           }}
//         >

//           {/* IMAGE CARD */}
//           <Paper
//             elevation={3}
//             sx={{
//               flex: 1,
//               borderRadius: 3,
//               overflow: "hidden",
//             }}
//           >
//             <Box
//               component="img"
//               src={trackingImg}
//               alt="Tracking"
//               sx={{
//                 width: "100%",
//                 height: { xs: 180, md: 260 },
//                 objectFit: "cover",
//               }}
//             />

//             <Box sx={{ p: 2 }}>
//               <Typography variant="h6" fontWeight="bold">
//                 Real-Time Shipment Tracking
//               </Typography>

//               <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
//                 Track your shipment from pickup to final delivery with live updates
//                 and full transparency across every stage.
//               </Typography>
//             </Box>
//           </Paper>

//           {/* INFO + BUTTON CARD */}
//           <Paper
//             elevation={2}
//             sx={{
//               flex: 1,
//               p: 3,
//               borderRadius: 3,
//               display: "flex",
//               flexDirection: "column",
//               justifyContent: "space-between",
//             }}
//           >
//             <Box>
//               <Typography fontWeight="bold" variant="h6">
//                 Shipment Details
//               </Typography>

//               <Divider sx={{ my: 2 }} />

//               <Typography><b>Status:</b> {currentStatus}</Typography>
//               <Typography><b>Sender:</b> {shipment.customer_name}</Typography>
//               <Typography><b>Receiver:</b> {shipment.receiver_name}</Typography>
//               <Typography><b>Trip:</b> {shipment.trip || "N/A"}</Typography>
//             </Box>

//             {/* DOWNLOAD BUTTON */}
//             <Box sx={{ mt: 3 }}>
//               <PDFDownloadLink
//                 document={<ShipmentReceipt shipment={shipment} />}
//                 fileName={`shipment-receipt-${shipment.tracking_code}.pdf`}
//               >
//                 {({ loading }) => (
//                   <Box
//                     sx={{
//                       background: "#111827",
//                       color: "#fff",
//                       textAlign: "center",
//                       py: 1.5,
//                       borderRadius: 2,
//                       fontWeight: "bold",
//                       cursor: "pointer",
//                     }}
//                   >
//                     {loading ? "Generating Receipt..." : "Download Shipping Receipt"}
//                   </Box>
//                 )}
//               </PDFDownloadLink>
//             </Box>
//           </Paper>
//         </Box>

//         {/* ================= DESKTOP TRACKER ================= */}
//         {!isMobile && (
//           <Box sx={{ position: "relative", mb: 6 }}>

//             <Box sx={{
//               position: "absolute",
//               top: 28,
//               left: 0,
//               right: 0,
//               height: 5,
//               background: "#e5e7eb",
//               zIndex: 1
//             }} />

//             <Box sx={{
//               position: "absolute",
//               top: 28,
//               left: 0,
//               width: `${(currentStepIndex / (steps.length - 1)) * 100}%`,
//               height: 5,
//               background: "#f59e0b",
//               zIndex: 2,
//               transition: "0.4s",
//             }} />

//             <Box sx={{
//               display: "flex",
//               justifyContent: "space-between",
//               position: "relative",
//               zIndex: 3
//             }}>
//               {steps.map((step, index) => {
//                 const isCurrent = step.key === currentStatus;
//                 const isPassed = index < currentStepIndex;

//                 return (
//                   <Box key={step.key} sx={{ textAlign: "center", width: "20%" }}>
//                     <Box
//                       sx={{
//                         width: 60,
//                         height: 60,
//                         borderRadius: "50%",
//                         mx: "auto",
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         background: isCurrent
//                           ? "#f59e0b"
//                           : isPassed
//                           ? "#dbeafe"
//                           : "#d1d5db",
//                         color: isPassed ? "#000" : "#fff",
//                       }}
//                     >
//                       {step.icon}
//                     </Box>

//                     <Typography sx={{ mt: 1 }}>
//                       {step.label}
//                     </Typography>
//                   </Box>
//                 );
//               })}
//             </Box>
//           </Box>
//         )}

//         {/* ================= MOBILE TRACKER ================= */}
//         {isMobile && (
//           <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: 2, mb: 6, pl: 3 }}>

//             <Box sx={{
//               position: "absolute",
//               left: 22,
//               top: 0,
//               bottom: 0,
//               width: 4,
//               background: "#e5e7eb",
//             }} />

//             <Box sx={{
//               position: "absolute",
//               left: 19,
//               top: currentStepIndex * 76,
//               width: 10,
//               height: 10,
//               borderRadius: "50%",
//               background: "#f59e0b",
//             }} />

//             {steps.map((step, index) => {
//               const isCurrent = step.key === currentStatus;
//               const isPassed = index < currentStepIndex;

//               return (
//                 <Box
//                   key={step.key}
//                   sx={{
//                     display: "flex",
//                     alignItems: "center",
//                     gap: 2,
//                     p: 2,
//                     borderRadius: 2,
//                     background: isCurrent ? "#fff7ed" : isPassed ? "#eff6ff" : "#f3f4f6",
//                   }}
//                 >
//                   <Box
//                     sx={{
//                       width: 45,
//                       height: 45,
//                       borderRadius: "50%",
//                       display: "flex",
//                       alignItems: "center",
//                       justifyContent: "center",
//                       background: isCurrent
//                         ? "#f59e0b"
//                         : isPassed
//                         ? "#dbeafe"
//                         : "#d1d5db",
//                       color: isPassed ? "#000" : "#fff",
//                     }}
//                   >
//                     {step.icon}
//                   </Box>

//                   <Typography>
//                     {step.label}
//                   </Typography>
//                 </Box>
//               );
//             })}
//           </Box>
//         )}



//       </Paper>
//     </Box>
//   );
// };

// export default TrackingContainer;

// import React, { useEffect, useState } from "react";
// import { useParams, useSearchParams } from "react-router-dom";
// import api from "../api/api";

// import {
//   Box,
//   Typography,
//   CircularProgress,
//   Paper,
//   Grid,
//   Divider,
//   Chip,
//   useMediaQuery,
// } from "@mui/material";

// import { useTheme } from "@mui/material/styles";

// import Inventory2Icon from "@mui/icons-material/Inventory2";
// import LocalShippingIcon from "@mui/icons-material/LocalShipping";
// import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
// import WarehouseIcon from "@mui/icons-material/Warehouse";
// import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// // ===============================
// const steps = [
//   { key: "PICKUP", label: "Pickup", icon: <Inventory2Icon /> },
//   { key: "LOADING", label: "Loading", icon: <LocalShippingIcon /> },
//   { key: "TRANSIT", label: "Transit", icon: <DirectionsBoatIcon /> },
//   { key: "ARRIVED", label: "Arrival", icon: <WarehouseIcon /> },
//   { key: "READY", label: "Ready", icon: <CheckCircleIcon /> },
//   { key: "CLOSED", label: "Closed", icon: <CheckCircleIcon /> },
// ];

// const TrackingContainer = () => {
//   const { code } = useParams();
//   const [searchParams] = useSearchParams();
//   const email = searchParams.get("email");

//   const [loading, setLoading] = useState(true);
//   const [shipment, setShipment] = useState(null);

//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

//   useEffect(() => {
//     const fetchTracking = async () => {
//       try {
//         let url = `/good-shipments/track/?code=${code}`;
//         if (email) url += `&email=${email}`;

//         const res = await api.get(url);
//         setShipment(res.data);
//       } catch (err) {
//         console.log(err);
//         setShipment(null);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchTracking();
//   }, [code, email]);

//   if (loading) {
//     return (
//       <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
//         <CircularProgress />
//       </Box>
//     );
//   }

//   if (!shipment) {
//     return (
//       <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
//         <Typography variant="h6">Tracking information not found.</Typography>
//       </Box>
//     );
//   }

//   const currentStatus = shipment.trip_status || shipment.tracking_status;

//   const currentStepIndex = steps.findIndex(
//     (step) => step.key === currentStatus
//   );

//   return (
//     <Box sx={{ minHeight: "100vh", background: "#f4f7fb", py: 5, px: 2 }}>
//       <Paper sx={{ maxWidth: 1100, mx: "auto", p: 4, borderRadius: 4 }}>

//         {/* HEADER */}
//         <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
//           <DirectionsBoatIcon sx={{ fontSize: 50, color: "#f59e0b" }} />
//    <Box>
//   <Typography
//     variant="h4"
//     fontWeight="bold"
//     sx={{
//       fontSize: { xs: "1.4rem", sm: "2.125rem" },
//       lineHeight: 1.2,
//       wordBreak: "break-word",
//     }}
//   >
//     Shipment Tracking
//   </Typography>

//   <Typography
//     color="text.secondary"
//     sx={{
//       fontSize: { xs: "0.85rem", sm: "1rem" },
//       wordBreak: "break-word",
//     }}
//   >
//     Tracking Code: {shipment.tracking_code}
//   </Typography>
// </Box>
//         </Box>

//         {/* STATUS */}
//         <Chip
//           label={currentStatus}
//           sx={{
//             mb: 4,
//             background: "#f59e0b",
//             color: "#fff",
//             fontWeight: "bold",
//           }}
//         />

//         {/* ===============================
//             DESKTOP TRACKER (UNCHANGED LOGIC + ENHANCED COLORS ONLY)
//         =============================== */}
//         {!isMobile && (
//           <Box sx={{ position: "relative", mb: 6 }}>

//             {/* BASE LINE */}
//             <Box sx={{
//               position: "absolute",
//               top: 28,
//               left: 0,
//               right: 0,
//               height: 5,
//               background: "#e5e7eb",
//               zIndex: 1
//             }} />

//             {/* ACTIVE LINE */}
//             <Box sx={{
//               position: "absolute",
//               top: 28,
//               left: 0,
//               width: `${(currentStepIndex / (steps.length - 1)) * 100}%`,
//               height: 5,
//               background: "#f59e0b",
//               zIndex: 2,
//               transition: "0.4s",
//             }} />

//             {/* STEPS */}
//             <Box sx={{
//               display: "flex",
//               justifyContent: "space-between",
//               position: "relative",
//               zIndex: 3
//             }}>
//               {steps.map((step, index) => {
//                 const isCurrent = step.key === currentStatus;
//                 const isPassed = index < currentStepIndex;
//                 const isFuture = index > currentStepIndex;

//                 return (
//                   <Box key={step.key} sx={{ textAlign: "center", width: "20%" }}>
//                     <Box
//                       sx={{
//                         width: 60,
//                         height: 60,
//                         borderRadius: "50%",
//                         mx: "auto",
//                         display: "flex",
//                         justifyContent: "center",
//                         alignItems: "center",

//                         // ✅ ADDED LOGIC ONLY
//                         background: isCurrent
//                           ? "#f59e0b"
//                           : isPassed
//                           ? "#dbeafe"
//                           : "#d1d5db",

//                         color: isPassed ? "#000" : "#fff",
//                       }}
//                     >
//                       {step.icon}
//                     </Box>

//                     <Typography
//                       sx={{
//                         mt: 1,
//                         fontWeight: isCurrent ? "bold" : "normal",
//                         color: isCurrent ? "#111827" : "#6b7280",
//                       }}
//                     >
//                       {step.label}
//                     </Typography>
//                   </Box>
//                 );
//               })}
//             </Box>
//           </Box>
//         )}

//         {/* ===============================
//             MOBILE TRACKER (ADDED HORIZONTAL LINE + COLORS)
//         =============================== */}
//         {isMobile && (
//           <Box
//             sx={{
//               position: "relative",
//               display: "flex",
//               flexDirection: "column",
//               gap: 2,
//               mb: 6,
//               pl: 3,
//             }}
//           >

//             {/* VERTICAL LINE (UNCHANGED) */}
//             <Box
//               sx={{
//                 position: "absolute",
//                 left: 22,
//                 top: 0,
//                 bottom: 0,
//                 width: 4,
//                 background: "#e5e7eb",
//                 borderRadius: 2,
//                 zIndex: 1,
//               }}
//             />

//             {/* 🔥 ADDED: HORIZONTAL LINE OVERLAY (DESKTOP STYLE FEEL IN MOBILE) */}
//             <Box
//               sx={{
//                 position: "absolute",
//                 left: 0,
//                 right: 0,
//                 top: "50%",
//                 height: 4,
//                 background: "#e5e7eb",
//                 zIndex: 1,
//                 opacity: 0.3,
//               }}
//             />

//             {/* MOVING INDICATOR (UNCHANGED) */}
//             <Box
//               sx={{
//                 position: "absolute",
//                 left: 19,
//                 top: currentStepIndex * 76,
//                 width: 10,
//                 height: 10,
//                 borderRadius: "50%",
//                 background: "#f59e0b",
//                 border: "3px solid #fff",
//                 boxShadow: "0 0 0 3px #f59e0b",
//                 zIndex: 3,
//                 transition: "0.4s",
//               }}
//             />

//             {steps.map((step, index) => {
//               const isCurrent = step.key === currentStatus;
//               const isPassed = index < currentStepIndex;
//               const isFuture = index > currentStepIndex;

//               return (
//                 <Box
//                   key={step.key}
//                   sx={{
//                     display: "flex",
//                     alignItems: "center",
//                     gap: 2,
//                     p: 2,
//                     borderRadius: 2,

//                     // unchanged background logic except pass styling refined
//                     background: isCurrent
//                       ? "#fff7ed"
//                       : isPassed
//                       ? "#eff6ff"
//                       : "#f3f4f6",

//                     border: isCurrent ? "3px solid #f59e0b" : "3px solid transparent",
//                     position: "relative",
//                     zIndex: 2,
//                   }}
//                 >
//                   <Box
//                     sx={{
//                       width: 45,
//                       height: 45,
//                       borderRadius: "50%",
//                       display: "flex",
//                       justifyContent: "center",
//                       alignItems: "center",

//                       // ✅ ADDED COLOR RULES
//                       background: isCurrent
//                         ? "#f59e0b"
//                         : isPassed
//                         ? "#dbeafe"
//                         : "#d1d5db",

//                       color: isPassed ? "#000" : "#fff",
//                     }}
//                   >
//                     {step.icon}
//                   </Box>

//                   <Typography
//                     sx={{
//                       fontWeight: isCurrent ? "bold" : "normal",
//                       color: isCurrent ? "#111827" : "#6b7280",
//                     }}
//                   >
//                     {step.label}
//                   </Typography>
//                 </Box>
//               );
//             })}
//           </Box>
//         )}

//         {/* ===============================
//             DETAILS (UNCHANGED)
//         =============================== */}
//         <Grid container spacing={3}>

//           <Grid item xs={12} md={6}>
//             <Paper sx={{ p: 3, borderRadius: 3 }}>
//               <Typography fontWeight="bold">Sender</Typography>
//               <Divider sx={{ my: 2 }} />

//               <Typography><b>Name:</b> {shipment.customer_name}</Typography>
//               <Typography><b>Trip:</b> {shipment.trip || "N/A"}</Typography>
//               <Typography><b>Amount:</b> ${shipment.amount_charge}</Typography>
//               <Typography><b>Discount:</b> ${shipment.discount}</Typography>

//               <Typography sx={{ mt: 2 }}>
//                 <b>Trip Status:</b> {shipment.trip_status || "N/A"}
//               </Typography>
//             </Paper>
//           </Grid>

//           <Grid item xs={12} md={6}>
//             <Paper sx={{ p: 3, borderRadius: 3 }}>
//               <Typography fontWeight="bold">Receiver</Typography>
//               <Divider sx={{ my: 2 }} />

//               <Typography><b>Name:</b> {shipment.receiver_name}</Typography>
//               <Typography><b>Contact:</b> {shipment.receiver_contact}</Typography>
//               <Typography><b>Address:</b> {shipment.receiver_address}</Typography>
//             </Paper>
//           </Grid>

//           <Grid item xs={12}>
//             <Paper sx={{ p: 3, borderRadius: 3 }}>
//               <Typography fontWeight="bold">Items</Typography>
//               <Divider sx={{ my: 2 }} />

//               <Typography sx={{ whiteSpace: "pre-line" }}>
//                 {shipment.items_name}
//               </Typography>
//             </Paper>
//           </Grid>

//         </Grid>
//       </Paper>
//     </Box>
//   );
// };

// export default TrackingContainer;

